import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useStores } from "@/lib/stores";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Truck, Package, DollarSign, Send, MapPin, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatDate, money } from "@/lib/utils-app";
import { logError } from "@/lib/logger";

const STATUS_LABEL = {
  awaiting_freight: "Aguardando taxa",
  awaiting_payment: "Aguardando pagamento",
  paid: "Frete pago",
  sent: "Enviado",
};
const STATUS_TONE = {
  awaiting_freight: "bg-amber-100 text-amber-800 border-amber-300",
  awaiting_payment: "bg-blue-100 text-blue-800 border-blue-300",
  paid: "bg-green-100 text-green-800 border-green-300",
  sent: "bg-neutral-800 text-white border-neutral-800",
};

export default function AdminShipments() {
  const { activeStore } = useStores();
  const [rows, setRows] = useState([]);
  const [freightMap, setFreightMap] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/shipments"); setRows(data); }
    catch (e) { logError("[shipments] load", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [activeStore]);

  const setFreight = async (code) => {
    const raw = freightMap[code];
    const amount = parseFloat(String(raw || "").replace(",", "."));
    if (!(amount > 0)) { toast.error("Digite um valor válido"); return; }
    try {
      await api.post(`/admin/service-orders/${code}/set-freight`, { amount });
      toast.success(`Taxa de R$ ${amount.toFixed(2)} confirmada. Cliente será notificado.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao confirmar frete"); }
  };

  const markSent = async (code) => {
    try {
      await api.post(`/admin/service-orders/${code}/mark-sent`);
      toast.success(`${code} marcada como enviada`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao marcar como enviada"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-shipments">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Solicitações de Envio</h1>
          <p className="text-sm text-neutral-500 mt-1">Central de logística — envios de cancelamentos e serviços concluídos.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="shipments-refresh">Atualizar</Button>
      </div>

      {rows.length === 0 && (
        <div className="border border-dashed border-neutral-300 rounded-2xl p-12 text-center text-neutral-500">
          <Truck size={32} className="mx-auto opacity-50"/>
          <div className="mt-3 text-sm">Nenhuma solicitação de envio no momento</div>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((o) => (
          <div key={o.os_code} className="bg-white border border-neutral-200 rounded-2xl p-5" data-testid={`shipment-${o.os_code}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-lg">{o.os_code}</span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${o.shipment_origin === "cancellation" ? "bg-red-50 text-red-700 border-red-200" : "bg-green-50 text-green-700 border-green-200"}`}>
                    {o.shipment_origin === "cancellation" ? <><Ban size={10} className="inline mr-1"/>Cancelamento</> : <><CheckCircle2 size={10} className="inline mr-1"/>Serviço Concluído</>}
                  </span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${STATUS_TONE[o.shipment_status] || ""}`}>
                    {STATUS_LABEL[o.shipment_status] || o.shipment_status}
                  </span>
                </div>
                <div className="mt-1 text-sm text-neutral-600">{o.customer_name} · {o.device_model}</div>
                <div className="text-xs text-neutral-500 font-mono">{o.customer_phone}</div>
              </div>
              <div className="text-right text-xs text-neutral-500">
                {o.freight_amount > 0 && <div>Frete: <b className="font-mono">{money(o.freight_amount)}</b></div>}
                <div>{formatDate(o.updated_at)}</div>
              </div>
            </div>

            {o.shipment_address && (
              <div className="mt-3 bg-neutral-50 rounded-lg p-3 text-xs">
                <div className="uppercase tracking-widest text-neutral-500 font-bold mb-1 flex items-center gap-1"><MapPin size={11}/> Endereço de entrega</div>
                <div>{o.shipment_address.street}, {o.shipment_address.number}{o.shipment_address.complement ? ` — ${o.shipment_address.complement}` : ""}</div>
                <div className="text-neutral-600">{o.shipment_address.neighborhood} · {o.shipment_address.city}/{o.shipment_address.state} · CEP {o.shipment_address.cep}</div>
              </div>
            )}

            {o.shipment_status === "awaiting_freight" && (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <div className="relative">
                  <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
                  <Input
                    placeholder="0,00"
                    value={freightMap[o.os_code] || ""}
                    onChange={(e) => setFreightMap((s) => ({ ...s, [o.os_code]: e.target.value }))}
                    className="w-40 h-10 pl-8 font-mono"
                    data-testid={`freight-input-${o.os_code}`}
                  />
                </div>
                <Button onClick={() => setFreight(o.os_code)} className="h-10 bg-neutral-900 hover:bg-neutral-800 text-white" data-testid={`freight-confirm-${o.os_code}`}>
                  Confirmar Taxa de Envio
                </Button>
              </div>
            )}
            {o.shipment_status === "paid" && (
              <Button onClick={() => markSent(o.os_code)} className="mt-4 h-10 bg-green-600 hover:bg-green-700 text-white" data-testid={`mark-sent-${o.os_code}`}>
                <Send size={14} className="mr-1.5"/> Aparelho Enviado
              </Button>
            )}
            {o.shipment_status === "sent" && (
              <div className="mt-3 text-xs text-green-700 flex items-center gap-1"><Package size={12}/> Enviado em {formatDate(o.shipment_sent_at)}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
